import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string;
  className?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  loading?: boolean;
  skeletonRows?: number;
  onRowClick?: (row: T) => void;
  empty?: { title: string; description?: string; action?: ReactNode };
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  loading,
  skeletonRows = 5,
  onRowClick,
  empty,
  className,
}: DataTableProps<T>) {
  const isEmpty = !loading && data.length === 0;
  const isClickable = Boolean(onRowClick);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border-subtle bg-white shadow-sm',
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-subtle bg-gray-50">
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  className={cn(
                    'px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted',
                    alignClass(c.align),
                    c.className,
                  )}
                >
                  {c.header}
                </th>
              ))}
              {isClickable && <th scope="col" className="w-8" aria-hidden />}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-b border-gray-100 last:border-0">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-2.5">
                      <Skeleton className="h-3 w-3/4" />
                    </td>
                  ))}
                  {isClickable && <td className="w-8" />}
                </tr>
              ))}

            {!loading &&
              data.map((row) => (
                <tr
                  key={keyExtractor(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'group border-b border-gray-100 last:border-0 transition-colors',
                    isClickable && 'cursor-pointer hover:bg-gray-50',
                  )}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'px-4 py-2.5 text-text-primary',
                        alignClass(c.align),
                        c.className,
                      )}
                    >
                      {c.render
                        ? c.render(row)
                        : ((row as Record<string, unknown>)[c.key] as ReactNode)}
                    </td>
                  ))}
                  {isClickable && (
                    <td className="w-8 pr-3">
                      <ChevronRight className="h-4 w-4 text-text-muted/60 transition-colors group-hover:text-accent" />
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {isEmpty && empty && (
        <EmptyState
          variant="list"
          title={empty.title}
          description={empty.description}
          action={empty.action}
        />
      )}
    </div>
  );
}

function alignClass(align: Column<unknown>['align']): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}
